import React from 'react';
import { Card } from '@/components/ui/card';
import { DigitalCardResponse } from '@/types';

interface PremiumDigitalCardProps {
  card?: DigitalCardResponse;
  htmlContent?: string;
}

const PremiumDigitalCard: React.FC<PremiumDigitalCardProps> = ({ card, htmlContent }) => {
  if (htmlContent) {
    return (
      <div
        className="premium-card-container"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  }

  const getStatusColor = (isActive: boolean, isExpired: boolean) => {
    if (isExpired) return 'from-red-500 to-red-600';
    if (isActive) return 'from-green-500 to-emerald-600';
    return 'from-gray-400 to-gray-500';
  };

  if (!card) {
    return <div className="text-center text-red-500">No card data available.</div>;
  }

  return (
    <div className="relative w-full max-w-[360px] mx-auto">
      <div className="relative aspect-[5/8] rounded-xl overflow-hidden shadow-xl">
        <div className={`absolute inset-0 bg-gradient-to-br ${getStatusColor(card.status_info.is_active, card.status_info.is_expired)}`} />
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" />
        
        <div className="relative h-full p-6 flex flex-col text-white">
          {/* Club Logo & Name */}
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <span className="text-2xl">🏀</span>
            </div>
            <div>
              <h3 className="text-lg font-bold">{card.template_data.system_name}</h3>
              <p className="text-sm opacity-90">Digital Membership Card</p>
            </div>
          </div>

          {/* Member Photo */}
          <div className="mb-6">
            {card.template_data.member.photo_url ? (
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white/30">
                <img 
                  src={card.template_data.member.photo_url} 
                  alt="Member" 
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-24 h-24 rounded-full bg-white/10 border-4 border-white/30 flex items-center justify-center">
                <span className="text-3xl">{card.template_data.initials}</span>
              </div>
            )}
          </div>

          {/* Member Info */}
          <div className="space-y-4 mb-auto">
            <div>
              <p className="text-sm opacity-70">Member Name</p>
              <p className="text-xl font-bold">{card.template_data.member.full_name}</p>
            </div>
            <div>
              <p className="text-sm opacity-70">Membership Type</p>
              <p className="text-lg">{card.template_data.card_data.member_type}</p>
            </div>
            {card.template_data.member.jersey_number && (
              <div>
                <p className="text-sm opacity-70">Jersey Number</p>
                <p className="text-lg">#{card.template_data.member.jersey_number}</p>
              </div>
            )}
            {card.template_data.member.playing_level && (
              <div>
                <p className="text-sm opacity-70">Playing Level</p>
                <p className="text-lg">{card.template_data.member.playing_level}</p>
              </div>
            )}
          </div>

          {/* Card Footer */}
          <div className="mt-6 space-y-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm opacity-70">Card Number</p>
                <p className="font-mono">{card.card.card_number}</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-70">Status</p>
                <p className="font-semibold">{card.status_info.status_text}</p>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm opacity-70">
              <span>Issued: {new Date(card.card.issued_at).toLocaleDateString()}</span>
              <span>Expires: {new Date(card.card.expiry_date).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumDigitalCard;
